// =====================================================================================
// INVRT Radar — web console — facet-backed filter field
//
// GET /v1/leads/facets serves the upstream provider's OWN vocabulary for a facet kind,
// and the response's `options` are deliberately untyped (`items: {}` in the OpenAPI
// document) — different kinds come back as plain strings or as objects. This field
// normalises both, and when the facet route is unavailable it degrades to free entry with
// the reason stated on screen rather than showing an empty picker that looks broken.
// =====================================================================================

import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import type { FacetKind, FacetSet } from "@/lib/types";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";

export interface FacetOption {
  value: string;
  label: string;
}

/** Reads one provider option, whatever shape it arrived in. */
export function normaliseFacetOption(option: unknown): FacetOption | null {
  if (typeof option === "string") {
    return option.trim() === "" ? null : { value: option, label: option };
  }
  if (typeof option === "number") return { value: String(option), label: String(option) };
  if (option === null || typeof option !== "object") return null;

  const record = option as Record<string, unknown>;
  const pick = (...keys: string[]): string | undefined => {
    for (const key of keys) {
      const candidate = record[key];
      if (typeof candidate === "string" && candidate.trim() !== "") return candidate;
      if (typeof candidate === "number") return String(candidate);
    }
    return undefined;
  };

  const value = pick("value", "id", "code", "name", "label");
  if (!value) return null;
  return { value, label: pick("label", "name", "display", "title") ?? value };
}

export function useFacet(kind: FacetKind, enabled: boolean) {
  const api = useApiClient();
  const { orgId } = useSession();

  return useQuery({
    queryKey: ["facets", orgId, kind],
    queryFn: () => api.get<FacetSet>("/leads/facets", { query: { kind } }),
    enabled: enabled && Boolean(orgId),
    staleTime: 30 * 60_000,
    retry: false,
  });
}

export interface FacetFieldProps {
  kind: FacetKind;
  label: string;
  hint?: string;
  /** Comma-separated in the free-entry fallback; a value list in the picker. */
  value: string[];
  onChange: (next: string[]) => void;
}

/**
 * A picker when the provider's vocabulary is available, a free-entry field when it is
 * not. Both write the same `string[]` back, so the caller never branches.
 */
export function FacetField({ kind, label, hint, value, onChange }: FacetFieldProps) {
  const { data, isLoading, isError } = useFacet(kind, true);

  const options = (data?.options ?? [])
    .map(normaliseFacetOption)
    .filter((option): option is FacetOption => option !== null);

  if (isLoading) {
    return (
      <Input
        label={label}
        value={value.join(", ")}
        onChange={(event) => onChange(splitList(event.target.value))}
        hint="Loading the provider's own list…"
        disabled
      />
    );
  }

  if (isError || options.length === 0) {
    return (
      <Input
        label={label}
        value={value.join(", ")}
        onChange={(event) => onChange(splitList(event.target.value))}
        placeholder="Separate several with commas"
        hint={
          isError
            ? "The provider's list is not available right now — type values instead, separated by commas."
            : (hint ?? "Separate several with commas.")
        }
      />
    );
  }

  const selectId = `facet-${kind}`;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={selectId} className="text-sm font-medium text-ink">
        {label}
      </label>
      <select
        id={selectId}
        multiple
        size={Math.min(6, Math.max(3, options.length))}
        value={value}
        onChange={(event) =>
          onChange(Array.from(event.target.selectedOptions).map((option) => option.value))
        }
        className="w-full rounded-lg border border-line bg-surface px-2 py-1.5 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <div className="flex items-center gap-2">
        <p className="text-xs text-ink-muted">
          {hint ?? "Hold Ctrl (or Cmd) to choose several."}
        </p>
        {data?.stale ? <Badge tone="warning">List is being refreshed</Badge> : null}
      </div>
    </div>
  );
}

export function splitList(raw: string): string[] {
  return raw
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "");
}
